
import React from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, MapPin, Linkedin, Youtube, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const Contact = () => {
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    toast.success("Message sent!", {
      description: "We'll get back to you as soon as possible."
    });
    // Reset form fields
    e.currentTarget.reset();
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Contact Upfixe | Get Support & Sales Inquiries</title>
        <meta name="description" content="Contact Upfixe for questions, support, or partnership inquiries. Reach our team directly through our contact form or email." />
        <meta name="keywords" content="contact Upfixe, customer support, sales inquiry, get help" />
        <meta property="og:title" content="Contact Upfixe | Get Support & Sales Inquiries" />
        <meta property="og:description" content="Contact Upfixe for questions, support, or partnership inquiries. Reach our team directly through our contact form or email." />
        <link rel="canonical" href="https://upfixe.com/contact" />
      </Helmet>
      <Header />
      <div className="container mx-auto px-4 py-12 pt-32 flex-grow">
        <h1 className="text-3xl font-bold mb-8">Contact Us</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <p className="text-lg mb-8">
              Have questions about Upfixe Ads? We're here to help! Reach out to our team using any of these methods.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-start">
                <Mail className="h-6 w-6 mr-4 text-primary" />
                <div>
                  <h3 className="font-semibold">Email</h3>
                  <p className="text-muted-foreground">
                    <a href="mailto:info@upfixe.com" className="hover:underline">
                      info@upfixe.com
                    </a>
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <MapPin className="h-6 w-6 mr-4 text-primary" />
                <div>
                  <h3 className="font-semibold">Location</h3>
                  <p className="text-muted-foreground">
                    Portugal
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <Linkedin className="h-6 w-6 mr-4 text-primary" />
                <div>
                  <h3 className="font-semibold">LinkedIn</h3>
                  <p className="text-muted-foreground">
                    <a 
                      href="https://www.linkedin.com/company/upfixe-ads" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:underline flex items-center"
                    >
                      linkedin.com/company/upfixe-ads
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <Youtube className="h-6 w-6 mr-4 text-primary" />
                <div>
                  <h3 className="font-semibold">YouTube</h3>
                  <p className="text-muted-foreground">
                    <a 
                      href="https://www.youtube.com/@upfixeads" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:underline flex items-center"
                    >
                      youtube.com/@upfixeads
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-12">
              <h2 className="text-2xl font-semibold mb-4">Support Hours</h2>
              <p className="text-muted-foreground mb-2">
                Monday - Friday: 9 AM - 6 PM (GMT)
              </p>
              <p className="text-sm text-muted-foreground">
                We typically respond to all inquiries within 24 hours during business days.
              </p>
            </div>
          </div>
          
          <div className="bg-card border rounded-lg p-6 shadow-sm">
            <h2 className="text-2xl font-semibold mb-6">Send Us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-1">
                  Your Name
                </label>
                <Input id="name" name="name" required />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1">
                  Email Address
                </label>
                <Input id="email" name="email" type="email" required />
              </div>
              
              <div>
                <label htmlFor="subject" className="block text-sm font-medium mb-1">
                  Subject
                </label>
                <Input id="subject" name="subject" required />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium mb-1">
                  Your Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  rows={5}
                  required
                  className="min-h-[120px]"
                />
              </div>
              
              <Button type="submit" className="w-full">
                Send Message
              </Button>
              
              <p className="text-xs text-muted-foreground mt-4">
                Your message will be sent to info@upfixe.com. We'll respond as soon as possible.
              </p>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;
